import {
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Box,
  Alert,
  Collapse,
} from '@mui/material';
import { memo, useMemo, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { CLAIM_TYPE_PROVIDER_CONFIG, getClaimTypeIcon } from '@claim-os/shared';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SectionHeading, GRID_SPACING, useTenantSettings } from '@claim-os/shared';

const ALL_CLAIM_TYPES = Object.entries(CLAIM_TYPE_PROVIDER_CONFIG).map(([code, config]) => ({
  code,
  label: config.label,
  description: config.description,
}));

interface ClaimTypeSectionProps {
  selectedTypes: string[];
  onToggle: (claimType: string) => void;
  /**
   * Returns the accordion panel content for a given claim type, or `undefined`
   * to keep the tile as click-to-toggle (no expansion). When the prop itself
   * is omitted, every tile is click-to-toggle.
   */
  renderExpanded?: (claimType: string) => ReactNode | undefined;
  /**
   * Optional summary text shown under the tile title when the tile is selected,
   * e.g. "Provider: Dr. Anne Simons" or "Primary Surgeon: Dr. Frank Blank".
   * When omitted (or returns undefined), the tile's normal description is shown.
   */
  renderSelectedSummary?: (claimType: string) => string | undefined;
  /** Auto-expand this claim type on mount (useful for the edit form). */
  defaultExpandedType?: string;
  error?: string;
  preserveClaimType?: string;
  skipTenantFilter?: boolean;
  title?: string;
  subtitle?: string;
  /**
   * Read-only mode for view/details pages. Cards lose their click affordance
   * (no CardActionArea ripple/cursor) and never expand — `renderExpanded` is
   * ignored. Selection highlight and `renderSelectedSummary` still apply.
   */
  readOnly?: boolean;
  /** Optional element rendered at the right of the section title (e.g. an edit action). */
  titleAction?: ReactNode;
}

const ClaimTypeSection = memo(function ClaimTypeSection({
  selectedTypes,
  onToggle,
  renderExpanded,
  renderSelectedSummary,
  defaultExpandedType,
  error,
  preserveClaimType,
  skipTenantFilter,
  title = 'Select the claim type and the providers you are billing for on this claim.',
  subtitle = 'Click a claim type to assign providers. Only one panel is open at a time.',
  readOnly = false,
  titleAction,
}: ClaimTypeSectionProps) {
  const { claimTypes: tenantClaimTypes, isLoading: settingsLoading } = useTenantSettings();
  const [expandedType, setExpandedType] = useState<string | null>(defaultExpandedType ?? null);
  const sectionRef = useRef<HTMLDivElement>(null);

  // Collapse the open provider panel when the user clicks outside the section.
  // MUI surfaces (the provider Autocomplete dropdown, menus, dialogs) are
  // portaled to <body> and so fall outside `sectionRef` — exclude them, or
  // picking a provider from the dropdown would collapse the panel mid-selection.
  useEffect(() => {
    if (!expandedType) return;
    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('.MuiPopover-root, .MuiPopper-root, .MuiModal-root, .MuiAutocomplete-popper')) {
        return;
      }
      if (sectionRef.current && !sectionRef.current.contains(target)) {
        setExpandedType(null);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [expandedType]);

  const CLAIM_TYPES = useMemo(() => {
    if (skipTenantFilter) return ALL_CLAIM_TYPES;
    if (settingsLoading) return [];
    if (tenantClaimTypes.length === 0) return ALL_CLAIM_TYPES;
    return ALL_CLAIM_TYPES.filter(
      (ct) => tenantClaimTypes.includes(ct.code) || ct.code === preserveClaimType,
    );
  }, [tenantClaimTypes, preserveClaimType, skipTenantFilter, settingsLoading]);

  const handleTileClick = useCallback(
    (code: string, isExpandable: boolean) => {
      if (isExpandable) {
        // Expandable tile: toggle expansion. Selection is driven by providers,
        // updated through the panel's own change events — not by this click.
        setExpandedType((current) => (current === code ? null : code));
        return;
      }
      // Non-expandable tile (FACILITY in claim forms; every tile on settings).
      // Per FR-004a, also collapse any currently expanded panel.
      setExpandedType(null);
      onToggle(code);
    },
    [onToggle],
  );

  if (CLAIM_TYPES.length === 0) {
    if (settingsLoading) return null;
    return (
      <>
        <SectionHeading title={title} subtitle={subtitle} />
        <Alert severity="error" sx={{ mt: 2 }}>
          No claim types are configured for your account. Contact your administrator.
        </Alert>
      </>
    );
  }

  return (
    <Box ref={sectionRef}>
      <SectionHeading title={title} subtitle={subtitle} action={titleAction} />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          // Top-align: each card sizes to its own content. Cards share a common
          // collapsed height via the header `minHeight` below (sized for a
          // two-provider summary), so they line up without any dynamic stretch.
          alignItems: 'start',
          gap: GRID_SPACING,
          mb: 2,
        }}
      >
        {CLAIM_TYPES.map((ct) => {
          const isSelected = selectedTypes.includes(ct.code);
          const panelContent = readOnly ? undefined : renderExpanded?.(ct.code);
          const isExpandable = panelContent != null;
          const isExpanded = isExpandable && expandedType === ct.code;
          const selectedSummary = isSelected ? renderSelectedSummary?.(ct.code) : undefined;

          // The selected tile fills black with white text; this styling rides
          // on the click surface (or its read-only stand-in) below.
          const surfaceSx = {
            alignItems: 'stretch',
            display: 'flex',
            // Height follows content: one row by default, growing only when
            // more than one provider is summarised.
            bgcolor: isSelected ? '#000000' : 'transparent',
            color: isSelected ? '#ffffff' : 'text.primary',
          } as const;

          // Card body — shared between the interactive (CardActionArea) and the
          // read-only (plain Box) wrappers below.
          const cardBody = (
                  <CardContent
                    sx={{
                      p: 2,
                      '&:last-child': { pb: 2 },
                      display: 'flex',
                      // Top-align so content sits at the top of the fixed-height
                      // header rather than centering in the reserved space.
                      alignItems: 'flex-start',
                      gap: 2,
                      width: '100%',
                    }}
                  >
                    {/* Icon container — 48x48; translucent-white chip with white icon when selected */}
                    <Box
                      sx={{
                        position: 'relative',
                        width: 48,
                        height: 48,
                        flexShrink: 0,
                        borderRadius: 1,
                        bgcolor: isSelected ? 'rgba(255, 255, 255, 0.2)' : '#f5f5f5',
                        color: isSelected ? '#fff' : 'text.secondary',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        alignSelf: 'flex-start',
                        fontSize: 24,
                      }}
                    >
                      {getClaimTypeIcon(ct.code)}
                    </Box>

                    {/* Title + description (or selected provider summary) */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 16,
                          fontWeight: 500,
                          color: isSelected ? '#ffffff' : 'text.primary',
                          lineHeight: '24px',
                          mb: 0.5,
                        }}
                      >
                        {ct.label}
                      </Typography>
                      {selectedSummary ? (
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 400,
                            color: '#ffffff',
                            lineHeight: '20px',
                            whiteSpace: 'pre-line',
                          }}
                        >
                          {selectedSummary}
                        </Typography>
                      ) : (
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 400,
                            color: isSelected ? '#ffffff' : 'text.secondary',
                            lineHeight: '20px',
                          }}
                        >
                          {ct.description}
                        </Typography>
                      )}
                    </Box>

                    {/* Right-aligned expand chevron, only for expandable tiles */}
                    {isExpandable && (
                      <ExpandMoreIcon
                        sx={{
                          color: isSelected ? '#ffffff' : 'text.secondary',
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                          flexShrink: 0,
                          // Content is top-aligned; keep the chevron centered vertically.
                          alignSelf: 'center',
                        }}
                      />
                    )}
                  </CardContent>
          );

          return (
            <Card
              key={ct.code}
              variant="outlined"
              sx={{
                borderColor: isSelected ? '#000000' : 'divider',
                bgcolor: 'background.paper',
                transition: 'all 0.15s',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              {readOnly ? (
                <Box sx={surfaceSx}>{cardBody}</Box>
              ) : (
                <CardActionArea
                  onClick={() => handleTileClick(ct.code, isExpandable)}
                  sx={{ ...surfaceSx, borderRadius: 0 }}
                >
                  {cardBody}
                </CardActionArea>
              )}

              {/* Provider panel — part of this (expanded) claim-type card. */}
              {isExpandable && (
                <Collapse in={isExpanded} unmountOnExit timeout="auto">
                  <Box sx={{ p: 2 }}>{panelContent}</Box>
                </Collapse>
              )}
            </Card>
          );
        })}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
});

export default ClaimTypeSection;
